import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { getRegister, RegisterEntry } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import {
  ChevronDown,
  ChevronRight,
  X,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Landmark,
  CreditCard,
  FolderOpen,
  Users,
  FileText,
} from 'lucide-react';
import {
  useContasPagarData,
  TipoGroup,
  TipoCompetencia,
} from '../hooks/useContasPagarData';

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status, hasDivergence }: { status: 'pago' | 'pendente' | 'parcial' | 'credito'; hasDivergence?: boolean }) {
  if (status === 'pago') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="h-3 w-3" /> Pago
      </span>
    );
  }
  if (status === 'credito') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="h-3 w-3" /> Crédito
      </span>
    );
  }
  if (status === 'parcial') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
        <AlertTriangle className="h-3 w-3" /> Parcial
        {hasDivergence && <AlertTriangle className="h-3 w-3 ml-0.5" />}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <Clock className="h-3 w-3" /> Pendente
      {hasDivergence && <AlertTriangle className="h-3 w-3 ml-0.5 text-yellow-600" />}
    </span>
  );
}

/** Color class for a Passivo balance: negative=devendo(red), positive=crédito(green), zero=gray */
function balanceColor(balance: number): string {
  if (Math.abs(balance) < 0.01) return 'text-gray-400';
  return balance < 0 ? 'text-red-700' : 'text-green-600';
}

// ---------------------------------------------------------------------------
// KPI mini card
// ---------------------------------------------------------------------------

function KpiMini({ label, value, color }: { label: string; value: string; color: string }) {
  const colorClasses: Record<string, string> = {
    red: 'border-l-red-500 text-red-700',
    blue: 'border-l-blue-500 text-blue-700',
    orange: 'border-l-orange-500 text-orange-700',
    gray: 'border-l-gray-400 text-gray-700',
  };
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${colorClasses[color] || colorClasses.gray} p-3 sm:p-4`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-lg sm:text-xl font-bold">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({
  icon,
  title,
  total,
  expanded,
  onToggle,
}: {
  icon: React.ReactNode;
  title: string;
  total: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-2 hover:bg-gray-50 -mx-2 px-2 rounded"
    >
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{title}</h3>
      </div>
      {Math.abs(total) >= 0.01 && (
        <span className={`text-sm font-bold ${balanceColor(total)}`}>
          {formatBRL(Math.abs(total))}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tipo card (e.g. INSS, IRRF, FGTS, Salários)
// ---------------------------------------------------------------------------

function TipoCard({
  group,
  onDrilldown,
}: {
  group: TipoGroup;
  onDrilldown: (account: string, label: string) => void;
}) {
  const hasPending = Math.abs(group.totalBalance) >= 0.01;
  const [expanded, setExpanded] = useState(hasPending);

  return (
    <div className="border border-gray-100 rounded-lg">
      {/* Tipo header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 rounded-lg"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <span className="text-sm font-semibold text-gray-800">{group.tipo}</span>
        </div>
        {Math.abs(group.totalBalance) >= 0.01 ? (
          <span className={`text-sm font-bold tabular-nums ${balanceColor(group.totalBalance)}`}>
            {formatBRL(Math.abs(group.totalBalance))}
          </span>
        ) : (
          <span className="text-xs text-gray-500 font-medium">Tudo pago</span>
        )}
      </button>

      {/* Competências */}
      {expanded && (
        <div className="px-4 pb-3 space-y-1">
          {group.competencias.map((comp) => (
            <CompetenciaLine
              key={comp.competencia}
              tipo={group.tipo}
              comp={comp}
              onDrilldown={onDrilldown}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Competência line within a tipo
// ---------------------------------------------------------------------------

function CompetenciaLine({
  tipo,
  comp,
  onDrilldown,
}: {
  tipo: string;
  comp: TipoCompetencia;
  onDrilldown: (account: string, label: string) => void;
}) {
  const [showItems, setShowItems] = useState(false);
  const hasItems = comp.items.length > 0;
  const isZero = comp.status === 'pago';

  return (
    <div>
      <div
        className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
          hasItems ? 'cursor-pointer hover:bg-gray-50' : ''
        } ${isZero ? 'opacity-60' : ''}`}
        onClick={() => hasItems && setShowItems((p) => !p)}
      >
        <div className="flex items-center gap-3">
          {hasItems ? (
            showItems ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />
          ) : (
            <span className="w-3" />
          )}
          <span className="font-mono text-xs text-gray-600">{comp.competencia}</span>
          <StatusBadge status={comp.status} hasDivergence={comp.hasDivergence} />
        </div>
        {!isZero && (
          <span className={`text-sm tabular-nums font-medium ${balanceColor(comp.balance)}`}>
            {formatBRL(Math.abs(comp.balance))}
          </span>
        )}
      </div>

      {/* Per-funcionário breakdown */}
      {showItems && hasItems && (
        <div className="ml-9 mb-1">
          {comp.items.map((item) => (
            <div
              key={item.full_path}
              className="flex items-center justify-between py-1 px-2 text-xs cursor-pointer hover:bg-blue-50 rounded"
              onClick={() => onDrilldown(item.full_path, `${tipo} - ${item.funcionario} (${comp.competencia})`)}
            >
              <span className="text-gray-700">{item.funcionario}</span>
              <span className={`tabular-nums font-medium ${balanceColor(item.balance)}`}>
                {formatBRL(Math.abs(item.balance))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ContasPagarPage() {
  const { data, loading, error } = useContasPagarData();

  // Section expansion
  const [showObrigacoes, setShowObrigacoes] = useState(true);
  const [showDAS, setShowDAS] = useState(true);
  const [showEmprestimos, setShowEmprestimos] = useState(true);
  const [showAdiantamentos, setShowAdiantamentos] = useState(true);
  const [showProvisaoFaturar, setShowProvisaoFaturar] = useState(true);
  const [showOutros, setShowOutros] = useState(true);

  // Drilldown modal
  const [drilldown, setDrilldown] = useState<{ account: string; label: string } | null>(null);
  const [drilldownEntries, setDrilldownEntries] = useState<RegisterEntry[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const openDrilldown = async (account: string, label: string) => {
    setDrilldown({ account, label });
    setDrilldownLoading(true);
    try {
      const result = await getRegister({ account, limit: 50 });
      setDrilldownEntries(result.entries);
    } catch {
      setDrilldownEntries([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Carregando contas a pagar..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data) return null;

  const { obrigacoes, emprestimos, das, adiantamentos, provisaoFaturar, outros, totals } = data;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiMini label="Total em Aberto" value={formatBRL(Math.abs(totals.grandTotal))} color="red" />
        <KpiMini label="Obrigações" value={formatBRL(Math.abs(totals.obrigacoes))} color="blue" />
        <KpiMini label="Empréstimos" value={formatBRL(Math.abs(totals.emprestimos))} color="orange" />
        <KpiMini label="DAS" value={formatBRL(Math.abs(totals.das))} color="gray" />
      </div>

      {/* Obrigações — grouped by tipo */}
      {obrigacoes.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<CreditCard className="h-4 w-4" />}
              title="Obrigações Trabalhistas"
              total={totals.obrigacoes}
              expanded={showObrigacoes}
              onToggle={() => setShowObrigacoes((p) => !p)}
            />
            {showObrigacoes && (
              <div className="mt-3 space-y-2">
                {obrigacoes.map((group) => (
                  <TipoCard key={group.tipo} group={group} onDrilldown={openDrilldown} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* DAS */}
      {das.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<CreditCard className="h-4 w-4" />}
              title="Impostos (DAS)"
              total={totals.das}
              expanded={showDAS}
              onToggle={() => setShowDAS((p) => !p)}
            />
            {showDAS && (
              <div className="mt-3">
                {das.map((row) => (
                  <div
                    key={row.full_path}
                    className={`flex items-center justify-between py-2 px-3 rounded text-sm cursor-pointer hover:bg-blue-50 ${
                      row.status === 'pago' ? 'opacity-60' : ''
                    }`}
                    onClick={() => openDrilldown(row.full_path, `DAS ${row.competencia}`)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-gray-600">{row.competencia}</span>
                      <StatusBadge status={row.status} />
                    </div>
                    {row.status !== 'pago' && (
                      <span className={`tabular-nums font-medium ${balanceColor(row.balance)}`}>
                        {formatBRL(Math.abs(row.balance))}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empréstimos */}
      {emprestimos.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<Landmark className="h-4 w-4" />}
              title="Empréstimos"
              total={totals.emprestimos}
              expanded={showEmprestimos}
              onToggle={() => setShowEmprestimos((p) => !p)}
            />
            {showEmprestimos && (
              <div className="mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500 text-xs">
                      <th className="text-left py-2 px-2">Banco</th>
                      <th className="text-left py-2 px-2">Contrato</th>
                      <th className="text-right py-2 px-2">Parcelas</th>
                      <th className="text-right py-2 px-2">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emprestimos.map((emp) => (
                      <tr
                        key={emp.full_path}
                        className="border-b border-gray-50 cursor-pointer hover:bg-blue-50"
                        onClick={() => openDrilldown(emp.full_path, `${emp.banco} - ${emp.contrato}`)}
                      >
                        <td className="py-2 px-2 font-medium text-gray-800">{emp.banco}</td>
                        <td className="py-2 px-2 font-mono text-xs text-gray-600">{emp.contrato}</td>
                        <td className="py-2 px-2 text-right text-gray-500">
                          {emp.parcelasRestantes > 0 ? `${emp.parcelasRestantes} restantes` : '-'}
                        </td>
                        <td className={`py-2 px-2 text-right tabular-nums font-bold ${balanceColor(emp.totalBalance)}`}>
                          {formatBRL(Math.abs(emp.totalBalance))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Adiantamentos de Clientes */}
      {adiantamentos.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<Users className="h-4 w-4" />}
              title="Adiantamentos de Clientes"
              total={totals.adiantamentos}
              expanded={showAdiantamentos}
              onToggle={() => setShowAdiantamentos((p) => !p)}
            />
            {showAdiantamentos && (
              <div className="mt-3">
                {adiantamentos.map((ad) => (
                  <div
                    key={ad.full_path}
                    className="flex items-center justify-between py-2 px-3 rounded text-sm cursor-pointer hover:bg-blue-50"
                    onClick={() => openDrilldown(ad.full_path, `Adiantamentos - ${ad.cliente}`)}
                  >
                    <span className="font-medium text-gray-800">{ad.cliente}</span>
                    <span className={`tabular-nums font-bold ${balanceColor(ad.balance)}`}>
                      {formatBRL(Math.abs(ad.balance))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Provisão a Faturar */}
      {provisaoFaturar.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<FileText className="h-4 w-4" />}
              title="Provisão a Faturar"
              total={totals.provisaoFaturar}
              expanded={showProvisaoFaturar}
              onToggle={() => setShowProvisaoFaturar((p) => !p)}
            />
            {showProvisaoFaturar && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">
                  Obrigação de emitir nota fiscal por trabalho já realizado.
                </p>
                {provisaoFaturar.map((prov) => (
                  <div key={prov.full_path}>
                    <div
                      className="flex items-center justify-between py-2 px-3 rounded text-sm cursor-pointer hover:bg-blue-50"
                      onClick={() => openDrilldown(prov.full_path, `Provisão a Faturar - ${prov.cliente}`)}
                    >
                      <span className="font-medium text-gray-800">{prov.cliente}</span>
                      <span className={`tabular-nums font-bold ${balanceColor(prov.balance)}`}>
                        {formatBRL(Math.abs(prov.balance))}
                      </span>
                    </div>
                    {prov.competencias.length > 0 && (
                      <div className="ml-6 mb-1">
                        {prov.competencias.map((comp) => (
                          <div
                            key={comp.full_path}
                            className="flex items-center justify-between py-1 px-2 text-xs cursor-pointer hover:bg-blue-50 rounded"
                            onClick={() => openDrilldown(comp.full_path, `${prov.cliente} (${comp.competencia})`)}
                          >
                            <span className="font-mono text-gray-600">{comp.competencia}</span>
                            <span className={`tabular-nums font-medium ${balanceColor(comp.balance)}`}>
                              {formatBRL(Math.abs(comp.balance))}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Outros */}
      {outros.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<FolderOpen className="h-4 w-4" />}
              title="Outros"
              total={totals.outros}
              expanded={showOutros}
              onToggle={() => setShowOutros((p) => !p)}
            />
            {showOutros && (
              <div className="mt-3">
                {outros.map((cat) => (
                  <div
                    key={cat.full_path}
                    className="flex items-center justify-between py-2 px-3 rounded text-sm cursor-pointer hover:bg-blue-50"
                    onClick={() => openDrilldown(cat.full_path, cat.name)}
                  >
                    <span className="font-medium text-gray-800">{cat.name}</span>
                    <span className={`tabular-nums font-bold ${balanceColor(cat.total)}`}>
                      {formatBRL(Math.abs(cat.total))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {obrigacoes.length === 0 && emprestimos.length === 0 && das.length === 0 && adiantamentos.length === 0 && provisaoFaturar.length === 0 && outros.length === 0 && (
        <EmptyState title="Nenhuma obrigação em aberto" description="Todos os passivos estão quitados." />
      )}

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">{drilldown.label}</h3>
              <button onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {drilldownLoading ? (
                <LoadingSpinner message="Carregando lançamentos..." />
              ) : drilldownEntries.length === 0 ? (
                <p className="text-gray-500 text-sm">Nenhum lançamento encontrado.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500 text-xs">
                      <th className="text-left py-2 px-2">Data</th>
                      <th className="text-left py-2 px-2">Descrição</th>
                      <th className="text-right py-2 px-2">Valor</th>
                      <th className="text-right py-2 px-2">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownEntries.map((entry, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-1.5 px-2 text-gray-600 whitespace-nowrap">{entry.date}</td>
                        <td className="py-1.5 px-2 text-gray-800 truncate max-w-xs">{entry.payee}</td>
                        <td className={`py-1.5 px-2 text-right tabular-nums whitespace-nowrap ${
                          entry.amount < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {entry.amount < 0 ? '-' : '+'}{formatBRL(Math.abs(entry.amount))}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-gray-500 whitespace-nowrap">
                          {formatBRL(Math.abs(entry.running_balance?.BRL || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
